import * as React from "react";
import Partner from "../../../../classes/Partner";
import AppContext from "../../AppContext";
import ContactListItem from "../ContactList/ContactListItem/ContactListItem";
import SectionLeads from "../../SectionLeads/SectionLeads";
import CompanySection from "../../Company/CompanySection/CompanySection";
import SectionTickets from "../../SectionTickets/SectionTickets";
import { ContentType, HttpVerb, requestWithAuth } from "../../../../utils/httpRequest";
import api from "../../../api";
import EnrichmentInfo, { EnrichmentInfoType } from "../../../../classes/EnrichmentInfo";
import { Spinner, SpinnerSize } from "office-ui-fabric-react";
import { OdooTheme } from "../../../../utils/Themes";
import "./ContactPage.css";
import { openOdooUrl } from "../../../../mock/mockInterceptor";
import Lead from "../../../../classes/Lead";
import HelpdeskTicket from "../../../../classes/HelpdeskTicket";
import SectionTasks from "../../SectionTasks/SectionTasks";
import Task from "../../../../classes/Task";
import Invoice from "../../../../classes/Invoice";
import SectionInvoices from "../../SectionInvoices/SectionInvoices";
import SaleOrder from "../../../../classes/SaleOrder";
import SectionSaleOrders from "../../SectionSaleOrders/SectionSaleOrders";
import SectionOpportunities from "../../SectionOpportunities/SectionOpportunities";
import PurchaseOrder from "../../../../classes/PurchaseOrder";
import SectionPurchaseOrders from "../../SectionPurchaseOrders/SectionPurchaseOrders";

type ContactPageProps = {
  partner: Partner;
  onPartnerChanged?: (Partner) => void;
  loadPartner: boolean;
};

type ContactPageState = {
  partner: Partner;
  canCreatePartner: boolean;
  isLoading: boolean;
  canCreateProject: boolean;
  isCrmInstalled?: boolean;
  isProjectInstalled?: boolean;
  isHelpdeskInstalled?: boolean;
  canSeeInvoices?: boolean;
  canSeeSaleOrders?: boolean;
  canSeePurchaseOrders?: boolean;
};

class ContactPage extends React.Component<ContactPageProps, ContactPageState> {
  constructor(props, context) {
    super(props, context);
    this.state = { partner: props.partner, isLoading: true, canCreatePartner: true, canCreateProject: true };
  }

  private async fetchPaginatedRecords<T>(
    endpoint: string,
    domain: any[],
    mapFn: (record: any) => T,
    maxRecords = 1000,
    pageSize = 200
  ): Promise<{ records: T[]; endpointReachable: boolean }> {
    let offset = 0;
    let loaded = 0;
    let records: T[] = [];
    let endpointReachable = false;

    while (loaded < maxRecords) {
      const request = requestWithAuth(
        HttpVerb.PATCH,
        api.baseURL + endpoint,
        ContentType.Json,
        { args: [domain], limit: pageSize, offset }
      );
      this.context.addRequestCanceller(request.cancel);

      const { responseText } = await request.promise;
      if (!responseText) break;

      const parsed = JSON.parse(responseText);
      if (!parsed.success || !Array.isArray(parsed.data)) break;

      endpointReachable = true;
      const batch = parsed.data.map(mapFn);
      records = records.concat(batch);
      loaded += batch.length;

      if (batch.length < pageSize) break;
      offset += pageSize;
    }

    return { records, endpointReachable };
  }

  private fetchLeads = async (partnerId?: number, email?: string): Promise<Lead[]> => {
    try {
      const normalizedEmail = (email || "").trim().toLowerCase();
      let domain: any[] = [];
      if (partnerId && partnerId > 0 && normalizedEmail) {
        domain = ["|", ["partner_id", "=", partnerId], ["email_from", "ilike", normalizedEmail]];
      } else if (partnerId && partnerId > 0) {
        domain = [["partner_id", "=", partnerId]];
      } else if (normalizedEmail) {
        domain = [["email_from", "ilike", normalizedEmail]];
      } else {
        this.setState({ isCrmInstalled: false });
        return [];
      }
      const { records: leads, endpointReachable } = await this.fetchPaginatedRecords(
        api.searchLeads,
        domain,
        lead_json => Lead.fromJSON(lead_json),
        1000,
        200
      );
      this.setState({ isCrmInstalled: endpointReachable });
      return leads;
    } catch (error) {
      this.setState({ isCrmInstalled: false });
      // Handle the error or return a default value if needed
      return [];
    }
  };

  private fetchTasks = async (partnerId: number): Promise<Task[]> => {
    try {
      const { records: tasks, endpointReachable } = await this.fetchPaginatedRecords(
        api.searchTasks,
        [["partner_id", "=", partnerId]],
        task_json => Task.fromJSON(task_json),
        1000,
        200
      );
      this.setState({ isProjectInstalled: endpointReachable });
      return tasks;
    } catch (error) {
      this.setState({ isProjectInstalled: false });
      // Handle the error or return a default value if needed
      return [];
    }
  };

  private fetchTickets = async (partnerId: number): Promise<HelpdeskTicket[]> => {
    try {
      const ticketsRequest = requestWithAuth(
        HttpVerb.PATCH,
        api.baseURL + api.searchTickets,
        ContentType.Json,
        { args: [[["partner_id", "=", partnerId]]] }
      );

      this.context.addRequestCanceller(ticketsRequest.cancel);

      const { responseText } = await ticketsRequest.promise;
      this.setState({ isHelpdeskInstalled: true });

      if (!responseText) return [];

      const parsed = JSON.parse(responseText);

      // Normalize Odoo boolean values
      const normalizeBoolean = (value: any): boolean => {
        if (value === true || value === 'True' || value === 'true' || value === 1) return true;
        if (value === false || value === 'False' || value === 'false' || value === 0) return false;
        return Boolean(value);
      };
      
      const isSuccess = normalizeBoolean(parsed.success);

      // Check for error response from backend
      if (!isSuccess && parsed.error) {
        this.context.showHttpErrorMessage(parsed);
        return [];
      }

      // Check if response has success and data structure
      let ticketIds = [];
      if (isSuccess && parsed.data) {
        if (Array.isArray(parsed.data)) {
          // If search_tickets returns array of ticket data or IDs
          ticketIds = parsed.data.map(ticket => ticket.id || ticket);
        } else if (typeof parsed.data === 'object' && parsed.data.id) {
          // If search_tickets returns a single ticket object
          ticketIds = [parsed.data.id];
        }
      } else if (Array.isArray(parsed)) {
        // Fallback for old format that returns array of IDs
        ticketIds = parsed;
      }

      if (ticketIds.length === 0) {
        return [];
      }

      // for tickets we can only search and not read the record in the same request
      // so we need to do a second request to get the ticket's data
      const ticketsRequests = ticketIds.map(ticketId => {
        return requestWithAuth(
          HttpVerb.PATCH,
          api.baseURL + api.readTicket.replace("{id}", ticketId.toString()),
          ContentType.Json
        );
      });

      const ticketsResponses = await Promise.all(ticketsRequests.map(request => request.promise));

      const parsedTickets = ticketsResponses.map(({ responseText }) => {
        const parsed = JSON.parse(responseText);
        
        // Normalize Odoo boolean values
        const normalizeBoolean = (value: any): boolean => {
          if (value === true || value === 'True' || value === 'true' || value === 1) return true;
          if (value === false || value === 'False' || value === 'false' || value === 0) return false;
          return Boolean(value);
        };
        
        const isSuccess = normalizeBoolean(parsed.success);
        
        // Handle both single object and array responses
        if (isSuccess && parsed.data) {
          return parsed.data;
        }
        return Array.isArray(parsed) ? parsed[0] : parsed;
      }).filter(ticket => ticket && ticket.id); // Filter out invalid tickets

      let tickets: HelpdeskTicket[] = [];
      try {
        tickets = parsedTickets.map(ticket_json => HelpdeskTicket.fromJSON(ticket_json));
      } catch (error) {
        console.log('Error parsing tickets:', error);
      }

      return tickets;
    } catch (error) {
      console.log(error);
      this.setState({ isHelpdeskInstalled: false });
      // Handle the error or return a default value if needed
      return [];
    }
  };

  private fetchInvoices = async (
    partnerId: number
  ): Promise<{ records: Invoice[]; endpointReachable: boolean }> => {
    try {
      let invoices: Invoice[] = [];
      let endpointReachable = false;

      const partnerInvoiceResult = await this.fetchPaginatedRecords(
        api.searchInvoices,
        [["partner_id", "=", partnerId]],
        invoice_json => Invoice.fromJSON(invoice_json),
        1000,
        200
      );
      invoices = partnerInvoiceResult.records;
      endpointReachable = partnerInvoiceResult.endpointReachable;

      this.setState({ canSeeInvoices: endpointReachable });
      return { records: invoices, endpointReachable };
    } catch (error) {
      console.log(error);
      this.setState({ canSeeInvoices: false });

      return { records: [], endpointReachable: false };
    }
  };

  private fetchSaleOrders = async (
    partnerId: number
  ): Promise<{ records: SaleOrder[]; endpointReachable: boolean }> => {
    try {
      const { records: saleOrders, endpointReachable } = await this.fetchPaginatedRecords(
        api.searchSaleOrders,
        [["partner_id", "=", partnerId]],
        order_json => SaleOrder.fromJSON(order_json),
        1000,
        200
      );
      this.setState({ canSeeSaleOrders: endpointReachable });
      return { records: saleOrders, endpointReachable };
    } catch (error) {
      console.log(error);
      this.setState({ canSeeSaleOrders: false });

      return { records: [], endpointReachable: false };
    }
  };

  private fetchPurchaseOrders = async (
    partnerId: number
  ): Promise<{ records: PurchaseOrder[]; endpointReachable: boolean }> => {
    try {
      const result = await this.fetchPaginatedRecords(
        api.searchPurchaseOrders,
        [["partner_id", "=", partnerId]],
        orderJson => PurchaseOrder.fromJSON(orderJson),
        1000,
        200
      );
      this.setState({ canSeePurchaseOrders: result.endpointReachable });
      return result;
    } catch (error) {
      console.log(error);
      this.setState({ canSeePurchaseOrders: false });
      return { records: [], endpointReachable: false };
    }
  };

  private fetchContact = () => {
    const partner = this.props.partner;
  
    // Don't fetch if partner doesn't exist in database (id: -1)
    if (!partner || partner.id === -1 || partner.id <= 0) {
      const normalizedEmail = (partner?.email || "").trim().toLowerCase();
      this.fetchLeads(undefined, normalizedEmail)
        .then(leads => {
          const partnerWithoutDbRecord = Object.assign(new Partner(), partner);
          partnerWithoutDbRecord.leads = leads;
          this.setState({
            isLoading: false,
            partner: partnerWithoutDbRecord,
            canCreatePartner: this.context.canCreatePartner !== false
          });
          this.propagatePartnerInfoChange(partnerWithoutDbRecord);
        })
        .catch(() => {
          this.setState({
            isLoading: false,
            partner: partner,
            canCreatePartner: this.context.canCreatePartner !== false
          });
          this.propagatePartnerInfoChange(partner);
        });
      return;
    }
  
    // Safety check: Never make API call with id: -1
    if (partner.id === -1 || partner.id <= 0) {
      this.setState({ isLoading: false, partner: partner });
      return;
    }
    
    const partnerRequest = requestWithAuth(
      HttpVerb.PATCH,
      api.baseURL + api.getPartner.replace("{id}", partner.id.toString()),
      ContentType.Json
    );
  
    this.context.addRequestCanceller(partnerRequest.cancel);
  
      partnerRequest.promise
        .then(async ({ responseText }) => {
          if (!responseText) {
            // Contact doesn't exist - reset id to -1 so it can be created
            const partnerNotFound = Object.assign(new Partner(), this.props.partner);
            partnerNotFound.id = -1;
            this.setState({ 
              partner: partnerNotFound, 
              isLoading: false,
              canCreatePartner: this.context.canCreatePartner !== false
            });
            this.propagatePartnerInfoChange(partnerNotFound);
            return;
          }
          const parsed = JSON.parse(responseText);
          
          // Normalize Odoo boolean values ('True'/'False' strings to boolean)
          const normalizeBoolean = (value: any): boolean => {
            if (value === true || value === 'True' || value === 'true' || value === 1) return true;
            if (value === false || value === 'False' || value === 'false' || value === 0) return false;
            return Boolean(value);
          };
          
          const isSuccess = normalizeBoolean(parsed.success);
          
          // Check for error response from backend
          if (!isSuccess && parsed.error) {
            this.context.showHttpErrorMessage(parsed);
            this.setState({ isLoading: false });
            return;
          }
          
          // Check if contact doesn't exist: no success, no data, empty array, empty object, or count is 0
          const hasNoData = !isSuccess || 
                           !parsed.data || 
                           (Array.isArray(parsed.data) && parsed.data.length === 0) ||
                           (typeof parsed.data === 'object' && !Array.isArray(parsed.data) && Object.keys(parsed.data).length === 0) ||
                           parsed.count === 0;
          
          if (hasNoData) {
            // Contact doesn't exist - reset id to -1 so it can be created
            const partnerNotFound = Object.assign(new Partner(), this.props.partner);
            partnerNotFound.id = -1;
            this.setState({ 
              partner: partnerNotFound, 
              isLoading: false,
              canCreatePartner: this.context.canCreatePartner !== false
            });
            this.propagatePartnerInfoChange(partnerNotFound);
            return;
          }
          
          // Handle case where data might be an array with one element (from search_read format)
          let partner = parsed.data;
          if (Array.isArray(parsed.data) && parsed.data.length > 0) {
            partner = parsed.data[0];
          } else if (Array.isArray(parsed.data) && parsed.data.length === 0) {
            // This should have been caught by hasNoData, but just in case
            const partnerNotFound = Object.assign(new Partner(), this.props.partner);
            partnerNotFound.id = -1;
            this.setState({ 
              partner: partnerNotFound, 
              isLoading: false,
              canCreatePartner: this.context.canCreatePartner !== false
            });
            this.propagatePartnerInfoChange(partnerNotFound);
            return;
          }
  
        const newPartner =
          partner && Object.keys(partner).length
            ? Partner.fromJSON(partner)
            : Partner.fromJSON({ email: partner.email, name: partner.name });
  
        const [leads, tasks, tickets, invoiceResult, saleOrderResult, purchaseOrderResult] = await Promise.all([
          this.fetchLeads(newPartner.id, newPartner.email || this.props.partner?.email),
          this.fetchTasks(newPartner.id),
          this.fetchTickets(newPartner.id),
          this.fetchInvoices(newPartner.id),
          this.fetchSaleOrders(newPartner.id),
          this.fetchPurchaseOrders(newPartner.id),
        ]);
  
        if (this.state.isCrmInstalled && leads) {
          newPartner.leads = leads;
        }
  
        if (this.state.isProjectInstalled && tasks) {
          newPartner.tasks = tasks;
        }
  
        if (this.state.isHelpdeskInstalled && tickets) {
          newPartner.tickets = tickets;
        }
  
        if (invoiceResult.endpointReachable) {
          newPartner.invoices = invoiceResult.records;
        }
  
        if (saleOrderResult.endpointReachable) {
          newPartner.saleOrders = saleOrderResult.records;
        }

        if (purchaseOrderResult.endpointReachable) {
          newPartner.purchaseOrders = purchaseOrderResult.records;
        }
  
        if (partner.user_companies) {
          this.context.setUserCompanies(partner.user_companies);
        }
  
        const canCreateProject = partner.can_create_project !== false;
        const canCreatePartner = partner.can_create_partner !== false;
  
        this.context.setCanCreatePartner(canCreatePartner);
        this.setState({
          partner: newPartner as Partner,
          isLoading: false,
          canCreatePartner: canCreatePartner,
          canCreateProject: canCreateProject,
          canSeeInvoices: invoiceResult.endpointReachable,
          canSeeSaleOrders: saleOrderResult.endpointReachable,
          canSeePurchaseOrders: purchaseOrderResult.endpointReachable,
        });
  
        if (partner["enrichment_info"]) {
          const enrichmentInfo = new EnrichmentInfo(partner["enrichment_info"].type, partner["enrichment_info"].info);
          if (enrichmentInfo.type !== EnrichmentInfoType.NoData) this.context.showTopBarMessage(enrichmentInfo);
        }
        this.propagatePartnerInfoChange(newPartner);
      })
      .catch((error) => {
        console.log(error);
        // If contact doesn't exist (404 or similar), reset id to -1 so it can be created
        // Don't show error message for "not found" cases - user should be able to create it
        const isNotFound = error?.status === 404 || error?.message?.includes('404') || error?.message?.includes('not found');
        if (isNotFound) {
          const partnerNotFound = Object.assign(new Partner(), this.props.partner);
          partnerNotFound.id = -1;
          this.setState({ 
            partner: partnerNotFound, 
            isLoading: false,
            canCreatePartner: this.context.canCreatePartner !== false
          });
          this.propagatePartnerInfoChange(partnerNotFound);
        } else {
          this.context.showHttpErrorMessage(error);
          this.setState({ isLoading: false });
        }
      });
  };  

  private propagatePartnerInfoChange = (partner: Partner) => {
    this.setState({ partner: partner });
    if (this.props.onPartnerChanged) {
      this.props.onPartnerChanged(partner);
    }
  };

  viewContact = partner => {
    if (!partner || partner.id === -1) return;
    const cids = this.context.getUserCompaniesString();
    const url = `${api.baseURL}/web#id=${partner.id}&model=res.partner&view_type=form${cids}`;
    openOdooUrl(url);
  };

  componentDidMount() {
    if (this.props.loadPartner && this.context.isConnected() && this.props.partner) {
      this.fetchContact();
    } else {
      this.setState({ isLoading: false, partner: this.props.partner });
    }
  }

  render() {
    if (this.state.isLoading) {
      return <Spinner className="contact-spinner" size={SpinnerSize.large} theme={OdooTheme} />;
    }

    return (
      <div className="contact-page">
        <div className="section-card">
          <ContactListItem
            partner={this.state.partner}
            canCreatePartner={this.state.canCreatePartner}
            onItemClick={this.viewContact}
          />
        </div>
        {this.state.isCrmInstalled && (
          <SectionLeads partner={this.state.partner} canCreatePartner={this.state.canCreatePartner} />
        )}
        {this.state.isCrmInstalled && (
          <SectionOpportunities partner={this.state.partner} canCreatePartner={this.state.canCreatePartner} />
        )}
        {this.state.isProjectInstalled && (
          <SectionTasks
            partner={this.state.partner}
            canCreatePartner={this.state.canCreatePartner}
            canCreateProject={this.state.canCreateProject}
          />
        )}
        {this.state.isHelpdeskInstalled && (
          <SectionTickets partner={this.state.partner} canCreatePartner={this.state.canCreatePartner} />
        )}
        {this.state.canSeeInvoices && (
          <SectionInvoices partner={this.state.partner} canCreatePartner={this.state.canCreatePartner} />
        )}
        {this.state.canSeeSaleOrders && (
          <SectionSaleOrders partner={this.state.partner} canCreatePartner={this.state.canCreatePartner} />
        )}
        {this.state.canSeePurchaseOrders && (
          <SectionPurchaseOrders partner={this.state.partner} canCreatePartner={this.state.canCreatePartner} />
        )}
        <CompanySection
          partner={this.state.partner}
          canCreatePartner={this.state.canCreatePartner}
          onPartnerInfoChanged={this.propagatePartnerInfoChange}
          hideCollapseButton={
            !(
              this.state.isCrmInstalled ||
              this.state.isProjectInstalled ||
              this.state.isHelpdeskInstalled ||
              this.state.canSeeInvoices ||
              this.state.canSeeSaleOrders ||
              this.state.canSeePurchaseOrders
            )
          }
        />
      </div>
    );
  }
}

ContactPage.contextType = AppContext;

export default ContactPage;
